import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import {
  IWebsiteBuilderSectionBase,
} from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { TWebsiteBuilderElement } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { TWebsiteBuilderContainer } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { IWebsiteBuilderEditorHistoryActionBase, IWebsiteBuilderHistorySnapshot, WebsiteBuilderEditorActionType } from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { TWebsiteBuilderSectionEntity } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";

export interface IBringToBackContainerActionPayload {
  id: string;
  parentId: string;
  originalZIndex: number;
  inverse: boolean;
}

export interface IBringToBackContainerAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.BRING_TO_BACK_CONTAINER;
  payload: IBringToBackContainerActionPayload;
}

export function getBringToBackContainerInverseAction(action: IBringToBackContainerAction): IBringToBackContainerAction {
  const { id, parentId, originalZIndex } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.BRING_TO_BACK_CONTAINER,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      id,
      parentId,
      originalZIndex,
      inverse: true,
    },
  };
}

function toggleBringToBackContainer<T extends IWebsiteBuilderSectionBase>(
  id: string,
  parentNode: T,
  originalZIndex: number,
  inverse: boolean,
) {
  return {
    ...parentNode,
    children: parentNode.children.map((child: TWebsiteBuilderSectionEntity): TWebsiteBuilderSectionEntity => {
      let zIndex: number = null;

      if (child.id === id) {
        zIndex = inverse ? originalZIndex : 0;
      } else {
        zIndex = (child.type === "element" ? child.element.zIndex : child.container.zIndex) ?? 0;
        zIndex += inverse ? -1 : 1;
      }

      if (child.type === "element") {
        const updatedElement: TWebsiteBuilderElement = {
          ...child,
        };

        updatedElement.element = {
          ...updatedElement.element,
          zIndex,
        };

        return updatedElement;
      } else {
        const updatedContainer: TWebsiteBuilderContainer = {
          ...child,
        };

        updatedContainer.container = {
          ...updatedContainer.container,
          zIndex,
        };

        return updatedContainer;
      }
    }),
  };
}

export function applyBringToBackContainerChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IBringToBackContainerAction,
): IWebsiteBuilderHistorySnapshot {
  const { id, parentId, originalZIndex, inverse } = action.payload;

  if (originalZIndex == null || originalZIndex === 0) {
    return { header, root, footer };
  }

  let updatedHeader = header;
  let updatedFooter = footer;
  let updatedRootChildren = root.children;

  if (header.id === parentId) {
    updatedHeader = toggleBringToBackContainer(id, updatedHeader, originalZIndex, inverse);
  } else if (footer.id === parentId) {
    updatedFooter = toggleBringToBackContainer(id, updatedFooter, originalZIndex, inverse);
  } else {
    updatedRootChildren = updatedRootChildren.map(child => {
      if (child.id === parentId) {
        return toggleBringToBackContainer(id, child, originalZIndex, inverse);
      } else {
        return child;
      }
    });
  }

  const updatedRoot: IWebsiteBuilderRoot = {
    ...root,
    children: updatedRootChildren,
  };

  return {
    header: updatedHeader,
    root: updatedRoot,
    footer: updatedFooter,
  };
}
